/**
 * Nota projetada Fuvest combinando 1ª e 2ª fase com pesos por curso.
 *
 * Fórmula Fuvest oficial (simplificada):
 *   - 1ª fase: número de acertos das 90 questões. Notas vão para escala 0-100.
 *   - 2ª fase: cada questão discursiva vale 0-10. Soma ponderada por
 *     disciplina conforme matriz do curso.
 *
 * Para Engenharia de Produção (Poli-USP), pesos atualizados pelo Manual do
 * Candidato 2026:
 *   - 1ª fase: peso 1
 *   - Matemática (2ª fase): peso 4
 *   - Física (2ª fase): peso 3
 *   - Química (2ª fase): peso 2
 *   - Outras disciplinas (2ª fase): peso 1 cada
 *
 * IMPORTANTE: pesos podem variar ano a ano. Esta implementação reflete o
 * manual 2026; o agente `fuvest-watcher` deve sinalizar mudanças anuais.
 *
 * Ref: docs/usp/Documento_A_PRD_Detalhado.docx + Manual do Candidato Fuvest.
 */

import type { PlatformTenantSlug } from "@/platform/lib/database.types";

export type FuvestDiscipline =
  | "matematica"
  | "fisica"
  | "quimica"
  | "biologia"
  | "portugues"
  | "literatura"
  | "ingles"
  | "historia"
  | "geografia"
  | "filosofia"
  | "sociologia"
  | "artes";

export type UspCurso =
  | "engenharia_producao"
  | "engenharia_civil"
  | "engenharia_eletrica"
  | "engenharia_mecanica"
  | "engenharia_quimica"
  | "computacao"
  | "medicina"
  | "direito"
  | "economia"
  | "outro";

/**
 * Pesos da 2ª fase por curso (apenas curso prioritário do produto Fase M4).
 * Para outros cursos USP, usar pesos default (peso 1 para todas).
 */
export const CURSO_WEIGHTS: Record<
  UspCurso,
  { fase1: number; disciplines: Partial<Record<FuvestDiscipline, number>> }
> = {
  engenharia_producao: {
    fase1: 1,
    disciplines: {
      matematica: 4,
      fisica: 3,
      quimica: 2,
      biologia: 1,
      portugues: 1,
      literatura: 1,
      ingles: 1,
      historia: 1,
      geografia: 1,
      filosofia: 1,
      sociologia: 1,
    },
  },
  engenharia_civil: { fase1: 1, disciplines: { matematica: 4, fisica: 3, quimica: 2 } },
  engenharia_eletrica: { fase1: 1, disciplines: { matematica: 4, fisica: 3, quimica: 2 } },
  engenharia_mecanica: { fase1: 1, disciplines: { matematica: 4, fisica: 3, quimica: 2 } },
  engenharia_quimica: { fase1: 1, disciplines: { matematica: 3, fisica: 2, quimica: 4 } },
  computacao: { fase1: 1, disciplines: { matematica: 4, fisica: 3 } },
  medicina: { fase1: 1, disciplines: { biologia: 4, quimica: 3, fisica: 2 } },
  direito: { fase1: 1, disciplines: { portugues: 3, historia: 3, geografia: 2 } },
  economia: { fase1: 1, disciplines: { matematica: 3, portugues: 2 } },
  outro: { fase1: 1, disciplines: {} },
};

/**
 * Notas de corte históricas Fuvest 2024 (informativo).
 * Atualizar via `fuvest-watcher` quando edital novo sair.
 */
export const NOTAS_CORTE_HISTORICAS: Record<UspCurso, number | null> = {
  engenharia_producao: 73.0,
  engenharia_civil: 68.5,
  engenharia_eletrica: 70.0,
  engenharia_mecanica: 70.5,
  engenharia_quimica: 65.0,
  computacao: 76.0,
  medicina: 88.5,
  direito: 75.0,
  economia: 73.5,
  outro: null,
};

export interface DiscursiveScoreInput {
  discipline: FuvestDiscipline;
  score: number; // 0-10
  maxScore: number; // tipicamente 10
}

export interface NotaProjetadaInput {
  curso: UspCurso;
  /** Acertos 1ª fase (0-90). */
  fase1Acertos: number;
  /** Lista de notas da 2ª fase, agregadas por disciplina. */
  fase2Scores: DiscursiveScoreInput[];
  /** Nota da redação (0-100). Conta na média 2ª fase como peso adicional. */
  redacaoScore?: number;
}

export interface NotaProjetada {
  /** Nota da 1ª fase em escala 0-100. */
  fase1Score100: number;
  /** Nota média da 2ª fase em escala 0-100. */
  fase2Score100: number;
  /** Média final ponderada. Escala 0-100. */
  notaFinal: number;
  /** Diferença vs nota de corte histórica (positivo = acima). */
  difCorte: number | null;
  /** Veredito comparativo. */
  veredito: "acima_corte" | "no_limite" | "abaixo_corte" | "sem_referencia";
  /** Decomposição pra UI / tooltip. */
  breakdown: {
    fase1Weight: number;
    fase2WeightedSum: number;
    fase2TotalWeight: number;
    disciplineDetails: Array<{
      discipline: FuvestDiscipline;
      score: number;
      weight: number;
      weighted: number;
    }>;
    redacaoIncluida: boolean;
  };
}

const REDACAO_DISC: FuvestDiscipline = "portugues"; // redação entra no peso de português

/**
 * Calcula nota projetada combinando 1ª e 2ª fase com pesos por curso.
 */
export function calcularNotaProjetada(input: NotaProjetadaInput): NotaProjetada {
  const cursoCfg = CURSO_WEIGHTS[input.curso];

  // 1ª fase: 0-90 acertos → 0-100
  const fase1Score100 = (input.fase1Acertos / 90) * 100;

  // 2ª fase: soma ponderada das disciplinas em escala 0-100
  const disciplineDetails = input.fase2Scores.map((s) => {
    const weight = cursoCfg.disciplines[s.discipline] ?? 1;
    const score100 = (s.score / s.maxScore) * 100;
    return {
      discipline: s.discipline,
      score: score100,
      weight,
      weighted: score100 * weight,
    };
  });

  const fase2WeightedSum = disciplineDetails.reduce((s, d) => s + d.weighted, 0);
  const fase2TotalWeight = disciplineDetails.reduce((s, d) => s + d.weight, 0);

  let fase2Score100 = fase2TotalWeight > 0 ? fase2WeightedSum / fase2TotalWeight : 0;

  // Redação Fuvest entra junto com Português na 2ª fase
  let redacaoIncluida = false;
  if (input.redacaoScore !== undefined) {
    const portWeight = cursoCfg.disciplines[REDACAO_DISC] ?? 1;
    const additional = input.redacaoScore * portWeight;
    fase2Score100 =
      (fase2WeightedSum + additional) / (fase2TotalWeight + portWeight || 1);
    redacaoIncluida = true;
  }

  // Média final: 1ª fase (peso fase1) + 2ª fase (peso 4 default Fuvest)
  // Fuvest geralmente usa fase 1 = 1x e fase 2 = 4x na composição final
  const fase1Weight = cursoCfg.fase1;
  const fase2WeightFinal = 4;
  const notaFinal =
    (fase1Score100 * fase1Weight + fase2Score100 * fase2WeightFinal) /
    (fase1Weight + fase2WeightFinal);

  // Diff vs corte
  const corte = NOTAS_CORTE_HISTORICAS[input.curso];
  const difCorte = corte !== null ? Number((notaFinal - corte).toFixed(2)) : null;
  let veredito: NotaProjetada["veredito"] = "sem_referencia";
  if (corte !== null) {
    if (notaFinal >= corte + 2) veredito = "acima_corte";
    else if (notaFinal >= corte - 2) veredito = "no_limite";
    else veredito = "abaixo_corte";
  }

  return {
    fase1Score100: Number(fase1Score100.toFixed(2)),
    fase2Score100: Number(fase2Score100.toFixed(2)),
    notaFinal: Number(notaFinal.toFixed(2)),
    difCorte,
    veredito,
    breakdown: {
      fase1Weight,
      fase2WeightedSum: Number(fase2WeightedSum.toFixed(2)),
      fase2TotalWeight,
      disciplineDetails: disciplineDetails.map((d) => ({
        ...d,
        score: Number(d.score.toFixed(2)),
        weighted: Number(d.weighted.toFixed(2)),
      })),
      redacaoIncluida,
    },
  };
}

/**
 * Cor associada ao veredito — usado em UI (badge, indicador).
 */
export function veredictoColor(v: NotaProjetada["veredito"]): { hsl: string; label: string } {
  switch (v) {
    case "acima_corte":
      return { hsl: "155 60% 38%", label: "Acima do corte" };
    case "no_limite":
      return { hsl: "40 85% 50%", label: "No limite" };
    case "abaixo_corte":
      return { hsl: "0 65% 50%", label: "Abaixo do corte" };
    case "sem_referencia":
    default:
      return { hsl: "220 10% 50%", label: "Sem referência" };
  }
}

// Guard de tenant — placeholder pra quando essa lib for usada em
// código compartilhado e precisar checar que está no escopo USP.
export const FUVEST_TENANT: PlatformTenantSlug = "usp";
